﻿import { LiveDataSet } from "../../API/LiveData/LiveDataSet";
import { ILiveDataListener } from "../../ILiveDataListener";
import { InfluxDataWrapper } from "../../InfluxDb/InfluxDataWrapper";
import { getGroupData } from "../../InfluxDb/InfluxDb";
import { MainSite } from "../../MainSite";
import { ISiteObservationsPage } from "./ISiteObservationsPage";

export class DirectionalPolarChart implements ILiveDataListener {
    private chartContainer: JQuery;
    private chart: __Highcharts.ChartObject;
    private stationId: string;
    private box: JQuery;
    private selectedHours: number;
    private valueId: string;
    private directionValueId: string;
    private anyData = false;
    private desiredPoints = 5000;
    private currentData: { timestamp: number, speed: number, direction: number }[] = [];

    constructor(
        private mainSite: MainSite, page: ISiteObservationsPage, container: JQuery,
        top: number, left: number, height: number, width: number,
        valueId: string, directionValueId: string,
        includeTimeSelector: boolean,
        private sensorId: string = null) {           
        this.stationId = page.station.Station.Id;
        this.selectedHours = 24; //initialization needed for the legend
        this.valueId = valueId;
        this.directionValueId = directionValueId;

        this.box = $('<div class="midi_SiteObservations_chart_container" id="plar-chart"></div>')
            .css('position', 'absolute')
            .css('top', top + '%')
            .css('left', left + '%')
            .css('height', height + '%')
            .css('width', width + '%');

        container.append(this.box);

        if (includeTimeSelector) {
            var timeSelector = this.createTimeSelector();
        }           

        this.chartContainer = $('<div></div>')
            .css('height', '83%')
            .appendTo(this.box);                                  

        this.chart = this.drawChart();

        //select default time period
        if (includeTimeSelector) {
            timeSelector.children('li').first().click();
        }        
    }

    public createTimeSelector(): JQuery {
        let allowedTimePeriods = [24, 168, 720];
        let timePeriodSelector = $('<div class="current_chart_selector"></div>')
            .css('margin-top', '10px')
            .append($('<span></span>').text("Time period: "))
            .appendTo(this.box);

        let tpul = $('<ul></ul>')
            .appendTo(timePeriodSelector);

        allowedTimePeriods.forEach((a) => {
            tpul.append($('<li></li>')
                .text(DirectionalPolarChart.formatHoursToDays(a))
                .attr('hours', a)
                .click((ev) => {
                    tpul.children('li').removeClass('selected');
                    $(ev.target).addClass("selected");
                    this.selectedHours = parseInt($(ev.target).attr('hours'));
                    this.updateChart(this.selectedHours, true);
                }));
        });

        return tpul;
    }

    private drawChart(): __Highcharts.ChartObject {
        this.chartContainer.highcharts(<any>{
            chart: {
                polar: true,
                marginLeft: 10,
                marginRight: 3,
            },
            exporting: {
                enabled: true
            },
            title: {
                text: ''
            },
            xAxis: {
                tickInterval: 22.5,
                min: 0,
                max: 360,
                labels: {
                    formatter: function () {
                        switch (this.value) {
                            case 0:
                                return 'N';
                            case 22.5:
                                return 'NNE';
                            case 45:
                                return 'NE';
                            case 67.5:
                                return 'ENE';
                            case 90:
                                return 'E';
                            case 112.5:
                                return 'ESE';
                            case 135:
                                return 'SE';
                            case 157.5:
                                return 'SSE';
                            case 180:
                                return 'S';
                            case 202.5:
                                return 'SSW';
                            case 225:
                                return 'SW';
                            case 247.5:
                                return 'WSW';
                            case 270:
                                return 'W';
                            case 292.5:
                                return 'WNW';
                            case 315:
                                return 'NW';
                            case 337.5:
                                return 'NNW';
                        }
                    }
                }
            },
            yAxis: {
                min: 0,
                labels: {
                    style: {
                        fontWeight: 'bold',
                    }
                }
            },
            legend: {
                align: 'center',
            },
            plotOptions: {
                series: {
                    pointStart: 0,
                    pointInterval: 45,
                    turboThreshold: 0,
                    states: {
                        hover: {
                            lineWidthPlus: 0 // disable line between dots on hover
                        }
                    },
                    marker: {
                        radius: 3
                    }
                },
                column: {
                    pointPadding: 0,
                    groupPadding: 0
                }
            },
            credits: {
                enabled: false
            },
            tooltip: {
                enabled: false
            },
        });

        return this.chartContainer.highcharts();
    }

    public setSelectedHours(hours: number) {
        this.selectedHours = hours;
    }

    public liveDataChanged(latestData: LiveDataSet): void {
        this.updateChart(this.selectedHours, false);
    }

    public updateChart(hours: number, reload: boolean): void {

        this.currentData = [];

        if (reload) {
            this.chart.showLoading('Getting data...');
        }

        if (!this.mainSite.liveDataManager.latestData) {
            return;
        }

        var toMoment = moment.utc();
        var fromMoment = moment.utc().subtract(hours, 'h');

        let latestDataFromStation = this.mainSite.liveDataManager.latestData.Stations[this.stationId];

        if (
            latestDataFromStation &&
            latestDataFromStation.GroupValues[this.valueId] &&
            latestDataFromStation.GroupValues[this.valueId].filter(v => v.Sensor == this.sensorId).length > 0 &&
            latestDataFromStation.GroupValues[this.directionValueId] &&
            latestDataFromStation.GroupValues[this.directionValueId].filter(v => v.Sensor == this.sensorId).length > 0
        ) {
            // If data is available for the this.sensorId, get the data and add to the chart

            // Get and handle speedData
            getGroupData(
                this.stationId,
                this.valueId,
                fromMoment.valueOf(),
                toMoment.valueOf(),
                (err: any, data: InfluxDataWrapper) => {
                    this.handleSpeedData(err, data, toMoment, fromMoment);
                },
                this.sensorId
            ).then(() => {
                // Get and handle directionalData
                getGroupData(
                    this.stationId,
                    this.directionValueId,
                    fromMoment.valueOf(),
                    toMoment.valueOf(),
                    (err: any, data: InfluxDataWrapper) => {
                        this.handleDirectionalData(err, data, toMoment, fromMoment);
                    },
                    this.sensorId
                ).done(() => {
                    // Finalize chart
                    this.finalizeDataAndUpdateChart(toMoment, fromMoment, hours);
                })
            });

        } else {
            // If data is not available, show information on chart

            this.chart.showLoading('Data not available');

            return;

        }
    }

    private handleSpeedData(err: any, data: InfluxDataWrapper, toMoment: moment.Moment, fromMoment: moment.Moment) {
        if (err) {
            console.warn('Polar Chart - Error getting data: ', err);
            this.chart.showLoading('Error getting data...');
            return;
        }

        this.anyData = this.anyData || data.Data !== null;

        if (data.Data !== null) {
            data.Data.unshift([fromMoment.valueOf(), null]);
            data.Data.push([toMoment.valueOf(), null]);
        }

        let pointSkip = Math.floor(data.Data.length / this.desiredPoints);

        data.Data.forEach((dataPoint, i) => {
            if (pointSkip !== 0 && i % pointSkip !== 0) {
                return;
            }
            let ts = dataPoint[0];
            let val = dataPoint[1];

            if (val) {
                this.currentData.push({ timestamp: ts, speed: val, direction: null });
            }
        });
    }

    private handleDirectionalData(err: any, data: InfluxDataWrapper, toMoment: moment.Moment, fromMoment: moment.Moment) {
        if (err) {
            console.warn('Polar Chart - Error getting data: ', err);
            this.chart.showLoading('Error getting data...');
            return;
        }

        this.anyData = this.anyData || data.Data !== null;

        if (data.Data !== null) {
            data.Data.unshift([fromMoment.valueOf(), null]);
            data.Data.push([toMoment.valueOf(), null]);
        }

        let pointSkip = Math.floor(data.Data.length / this.desiredPoints);

        data.Data.forEach((dataPoint, i) => {
            if (pointSkip !== 0 && i % pointSkip !== 0) {
                return;
            }
            let ts = dataPoint[0];
            let val = dataPoint[1];

            let x = this.currentData.filter(x => x.timestamp === ts);

            if (x.length) {
                x[0].direction = val;
            }
        });
    }

    private finalizeDataAndUpdateChart(toMoment: moment.Moment, fromMoment: moment.Moment, hours: number) {
        let filteredCurrentData = this.currentData.filter(x =>
                Number.isFinite(x.speed) && Number.isFinite(x.direction) &&
                x.speed >= 0 && x.speed < 100 &&
                x.direction >= 0 && x.direction <= 360
            );

        if (!filteredCurrentData.length) {
            this.chart.showLoading('Data not available');
            return;
        }

        filteredCurrentData.sort((a, b) => {
            return a.direction - b.direction;
        });

        //draw chart
        let polarData: { x: number, y: number}[] = [];

        //the latest values must be drawen over the old values (points are sorted by direction, by x axis)
        let latestPolarData: { x: number, y: number }[] = [];

        filteredCurrentData.forEach((d, index) => {
            let latest = d.timestamp < toMoment.unix() * 1000 && (d.timestamp > (toMoment.unix() * 1000 - (hours / 12) * 3600000));
            if (latest) {
                latestPolarData.push({ x: d.direction, y: d.speed });
            } else {
                polarData.push({ x: d.direction, y: d.speed });
            }
        });

        this.chart.hideLoading();
        let polarSeries = {
            id: "Polar_Series",
            name: "All",
            lineWidth: 0,
            data: polarData,
            color: '#fbce07',
        };

        let latestPolarSeries = {
            id: "Latest_Polar_Series",
            name: "Latest",
            lineWidth: 0,
            color: '#f54e42',
            data: latestPolarData,
        };

        if (this.chart.series.length < 1) {
            this.chart.addSeries(polarSeries);
            this.chart.addSeries(latestPolarSeries);
        }
        else {
            let cs: __Highcharts.SeriesObject = <__Highcharts.SeriesObject>this.chart.get("Polar_Series");
            cs.setData(<any>polarSeries.data);

            let latestCs: __Highcharts.SeriesObject = <__Highcharts.SeriesObject>this.chart.get("Latest_Polar_Series");
            latestCs.setData(<any>latestPolarSeries.data);
        }

        this.chart.update(<any>{
            legend: {
                labelFormatter: function () {
                    if (this.name == "All") {
                        return "Greater than " + (hours / 12) + " hours";
                    } else {
                        return "Less than " + (hours / 12) + " hours";
                    }
                }
            },
        });

        this.chart.redraw();
    }

    public static formatHoursToDays(hours: number): string {
        let days = hours / 24;
        if (days === 1) {
            return days + " day";
        }
        return days + " days";
    }
}